import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { jobsAPI, applicationsAPI } from '../services/api';
import {
  MapPin,
  Briefcase,
  DollarSign,
  Clock,
  Users,
  BookmarkPlus,
  Share2,
  Building2,
  GraduationCap,
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Laptop,
  Landmark,
  Heart,
  ShoppingBag,
  Factory,
  Plane,
  Truck,
  Utensils,
  Lightbulb,
  Radio,
  Shield,
  Hammer,
  Palette,
  Leaf,
  Stethoscope,
  Scale,
  Film
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

// Industry icon mapping with colors
const getIndustryConfig = (industry) => {
  const industryLower = (industry || '').toLowerCase();
  
  if (industryLower.includes('technology') || industryLower.includes('it ') || industryLower.includes('software') || industryLower.includes('consulting')) {
    return { icon: Laptop, bgFrom: 'from-blue-100', bgTo: 'to-blue-200', iconColor: 'text-blue-600' };
  }
  if (industryLower.includes('banking') || industryLower.includes('finance') || industryLower.includes('fintech')) {
    return { icon: Landmark, bgFrom: 'from-green-100', bgTo: 'to-green-200', iconColor: 'text-green-600' };
  }
  if (industryLower.includes('healthcare') || industryLower.includes('hospital') || industryLower.includes('medical') || industryLower.includes('pharma')) {
    return { icon: Stethoscope, bgFrom: 'from-red-100', bgTo: 'to-red-200', iconColor: 'text-red-600' };
  }
  if (industryLower.includes('education') || industryLower.includes('edtech') || industryLower.includes('training')) {
    return { icon: GraduationCap, bgFrom: 'from-yellow-100', bgTo: 'to-yellow-200', iconColor: 'text-yellow-600' };
  }
  if (industryLower.includes('retail') || industryLower.includes('ecommerce') || industryLower.includes('e-commerce') || industryLower.includes('fmcg')) {
    return { icon: ShoppingBag, bgFrom: 'from-pink-100', bgTo: 'to-pink-200', iconColor: 'text-pink-600' };
  }
  if (industryLower.includes('manufacturing') || industryLower.includes('industrial') || industryLower.includes('automotive')) {
    return { icon: Factory, bgFrom: 'from-gray-100', bgTo: 'to-gray-200', iconColor: 'text-gray-600' };
  }
  if (industryLower.includes('travel') || industryLower.includes('aviation') || industryLower.includes('hospitality') || industryLower.includes('tourism')) {
    return { icon: Plane, bgFrom: 'from-cyan-100', bgTo: 'to-cyan-200', iconColor: 'text-cyan-600' };
  }
  if (industryLower.includes('logistics') || industryLower.includes('supply chain') || industryLower.includes('transport')) {
    return { icon: Truck, bgFrom: 'from-orange-100', bgTo: 'to-orange-200', iconColor: 'text-orange-600' };
  }
  if (industryLower.includes('food') || industryLower.includes('restaurant') || industryLower.includes('beverage')) {
    return { icon: Utensils, bgFrom: 'from-amber-100', bgTo: 'to-amber-200', iconColor: 'text-amber-600' };
  }
  if (industryLower.includes('energy') || industryLower.includes('power') || industryLower.includes('oil') || industryLower.includes('gas')) {
    return { icon: Lightbulb, bgFrom: 'from-yellow-100', bgTo: 'to-yellow-200', iconColor: 'text-yellow-500' };
  }
  if (industryLower.includes('media') || industryLower.includes('entertainment') || industryLower.includes('broadcast')) {
    return { icon: Film, bgFrom: 'from-purple-100', bgTo: 'to-purple-200', iconColor: 'text-purple-600' };
  }
  if (industryLower.includes('telecom') || industryLower.includes('communication')) {
    return { icon: Radio, bgFrom: 'from-indigo-100', bgTo: 'to-indigo-200', iconColor: 'text-indigo-600' };
  }
  if (industryLower.includes('insurance') || industryLower.includes('security')) {
    return { icon: Shield, bgFrom: 'from-teal-100', bgTo: 'to-teal-200', iconColor: 'text-teal-600' };
  }
  if (industryLower.includes('construction') || industryLower.includes('real estate') || industryLower.includes('infrastructure')) {
    return { icon: Hammer, bgFrom: 'from-stone-100', bgTo: 'to-stone-200', iconColor: 'text-stone-600' };
  }
  if (industryLower.includes('design') || industryLower.includes('creative') || industryLower.includes('advertising') || industryLower.includes('marketing')) {
    return { icon: Palette, bgFrom: 'from-fuchsia-100', bgTo: 'to-fuchsia-200', iconColor: 'text-fuchsia-600' };
  }
  if (industryLower.includes('agriculture') || industryLower.includes('farming') || industryLower.includes('agri')) {
    return { icon: Leaf, bgFrom: 'from-lime-100', bgTo: 'to-lime-200', iconColor: 'text-lime-600' };
  }
  if (industryLower.includes('legal') || industryLower.includes('law')) {
    return { icon: Scale, bgFrom: 'from-slate-100', bgTo: 'to-slate-200', iconColor: 'text-slate-600' };
  }
  if (industryLower.includes('ngo') || industryLower.includes('non-profit') || industryLower.includes('charity')) {
    return { icon: Heart, bgFrom: 'from-rose-100', bgTo: 'to-rose-200', iconColor: 'text-rose-600' };
  }
  
  // Default
  return { icon: Building2, bgFrom: 'from-purple-100', bgTo: 'to-purple-200', iconColor: 'text-purple-600' };
};

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');

    // Job detail is public, but applying requires login
    if (!token) {
      console.log('User not logged in');
    }

    if (token && role !== 'job_seeker') {
      console.log('Only job seekers can apply');
    }
  }, []);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApplied, setIsApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    checkLoginStatus();
  }, [id]);

  const checkLoginStatus = () => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');
    setIsLoggedIn(!!token && role === 'job_seeker');
  };

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getById(id);
      setJob(response?.data || null );
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load job details.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    // Check if user is logged in as job seeker
    if (!isLoggedIn) {
      toast({
        title: 'Login Required',
        description: 'Please login as a job seeker to apply for this job.',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }

    try {
      setApplying(true);
      await applicationsAPI.applyToJob(id, '');
      setIsApplied(true);
      toast({
        title: 'Application Submitted!',
        description: 'Your application has been submitted successfully.',
      });
      // Refresh job details to get updated applicant count
      fetchJobDetails();
    } catch (error) {
      console.error('Error applying to job:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit application. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setApplying(false);
    }
  };

  const handleSave = () => {
    toast({
      title: 'Job Saved!',
      description: 'This job has been added to your saved jobs.',
    });
  };

  const handleShare = () => {
    toast({
      title: 'Link Copied!',
      description: 'Job link has been copied to clipboard.',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#4a90e2] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-800">Job not found</h2>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const industry = job.industryType || job.industry || 'IT Services & Consulting';
  const { icon: IndustryIcon, bgFrom, bgTo, iconColor } = getIndustryConfig(industry);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-600 hover:text-[#4a90e2]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="p-8 mb-6">
              <div className="flex gap-6 mb-6">
                <div className="flex-shrink-0">
                  <div className={`w-20 h-20 bg-gradient-to-br ${bgFrom} ${bgTo} rounded-lg flex items-center justify-center overflow-hidden`}>
                    <IndustryIcon className={`w-10 h-10 ${iconColor}`} />
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">{job.title}</h1>
                  {/* Show Industry instead of Company Name */}
                  <p className={`text-xl font-medium mb-4 ${iconColor}`}>{industry}</p>

                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center text-gray-600">
                      <Briefcase className="w-5 h-5 mr-2 text-gray-500" />
                      {job.experience}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <DollarSign className="w-5 h-5 mr-2 text-gray-500" />
                      {job.salary}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2 text-gray-500" />
                      {job.location}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(job.skills || []).map((skill, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-50 text-blue-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between py-4 border-t border-b border-gray-200 mb-6">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Posted {job.postedDays} days ago
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    {job.applicants} applicants
                  </div>
                  <Badge className="bg-green-50 text-green-700 border-green-200">
                    {job.jobType}
                  </Badge>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={handleSave}>
                    <BookmarkPlus className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Job Description</h2>
                <p className="text-gray-600 leading-relaxed mb-4">{job.description}</p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We are looking for a talented professional to join our dynamic team. The ideal candidate
                  will have strong technical skills and the ability to work in a fast-paced environment.
                </p>
              </div>

              {job.responsibilities && job.responsibilities.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Key Responsibilities</h2>
                  <ul className="space-y-3">
                    {job.responsibilities.map((responsibility, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{responsibility}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {job.requiredSkills && job.requiredSkills.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Required Skills</h2>
                  <ul className="space-y-3">
                    {job.requiredSkills.map((skill, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{skill}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {job.education && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2 text-[#4a90e2]" />
                    Education
                  </h2>
                  <p className="text-gray-600">{job.education}</p>
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24 mb-6">
              {!isApplied ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Ready to apply?</h3>
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    className="w-full bg-[#4a90e2] hover:bg-[#3a7bc8] text-white font-semibold mb-3 h-12"
                  >
                    {applying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      'Apply Now'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleSave}
                    className="w-full border-[#4a90e2] text-[#4a90e2] hover:bg-[#4a90e2] hover:text-white"
                  >
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                    Save Job
                  </Button>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Application Submitted!</h3>
                  <p className="text-sm text-gray-600">We'll notify you about the next steps.</p>
                </div>
              )}
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <IndustryIcon className={`w-5 h-5 ${iconColor}`} />
                <h3 className="text-lg font-semibold text-gray-800">About this Role</h3>
              </div>
              <div className={`w-20 h-20 bg-gradient-to-br ${bgFrom} ${bgTo} rounded-lg flex items-center justify-center mb-4 overflow-hidden`}>
                <IndustryIcon className={`w-10 h-10 ${iconColor}`} />
              </div>
              <p className={`font-medium mb-2 ${iconColor}`}>{industry}</p>
              <p className="text-gray-600 text-sm mb-4">
                This role is in the {industry} sector, offering excellent career opportunities
                and a great work environment.
              </p>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                  {job.jobType}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  {job.location}
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                  {job.salary}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JobDetail;
